import { createAjv } from "@jsonforms/core";
import { findActiveBranchIndex } from "./schemaToRows";
import { pathToId } from "./types";

export interface FormError {
  rowId: string;
  message: string;
  raw: any;
}

export type ErrorSeverity = "error" | "warning";

export interface ValidationResult {
  errors: FormError[];
  blockingErrors: FormError[];
  errorsByRowId: Map<string, { message: string; severity: ErrorSeverity }>;
}

const SCHEMA_FLAG_KEYS = new Set([
  "__optionalSection",
  "__fromAdditionalProperties",
  "__anyOfTransformed",
  "use_endpoint",
]);

function stripFlags(schema: any): any {
  if (!schema || typeof schema !== "object") return schema;
  if (Array.isArray(schema)) return schema.map(stripFlags);
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(schema)) {
    if (SCHEMA_FLAG_KEYS.has(k)) continue;
    out[k] = stripFlags(v);
  }
  return out;
}

function instancePathToRowId(
  instancePath: string,
  missingProperty?: string
): string {
  const parts = instancePath.split("/").filter(Boolean);
  if (missingProperty) parts.push(missingProperty);
  if (parts.length === 0) return "__root__";
  return parts.map((p) => p).join(".");
}

function isInactiveBranchError(
  error: any,
  schema: any,
  data: any,
  propKey: string
): boolean {
  const m = error?.schemaPath?.match(
    new RegExp(`#/properties/${propKey}/oneOf/(\\d+)/`)
  );
  if (!m) return false;
  const branchIndex = Number(m[1]);
  if (Number.isNaN(branchIndex)) return false;
  const activeIndex = findActiveBranchIndex(
    schema.properties[propKey],
    data?.[propKey]
  );
  return activeIndex !== branchIndex;
}

function filterOneOfBranchErrors(
  errors: any[],
  schema: any,
  data: any
): any[] {
  if (!schema?.properties) return errors;
  const oneOfPropKeys = Object.keys(schema.properties).filter(
    (k) => Array.isArray(schema.properties[k]?.oneOf)
  );
  return errors.filter((error) => {
    if (!error) return false;
    if (error.keyword === "oneOf") return false;
    for (const k of oneOfPropKeys) {
      if (isInactiveBranchError(error, schema, data, k)) return false;
    }
    return true;
  });
}

// Step from schemaNode to property/array-item `key`, routing through the active
// oneOf branch so deep required errors aren't misread as "optional empty parent".
function navigateSchema(schemaNode: any, dataAtNode: any, key: string): any {
  if (schemaNode?.properties?.[key]) return schemaNode.properties[key];
  if (/^\d+$/.test(key) && schemaNode?.items && !Array.isArray(schemaNode.items)) {
    return schemaNode.items;
  }
  if (Array.isArray(schemaNode?.oneOf)) {
    const activeIndex = findActiveBranchIndex(schemaNode, dataAtNode);
    const branch = activeIndex >= 0 ? schemaNode.oneOf[activeIndex] : undefined;
    if (branch?.properties?.[key]) return branch.properties[key];
  }
  return undefined;
}

function getRequired(schemaNode: any, dataAtNode: any): string[] {
  if (Array.isArray(schemaNode?.required)) return schemaNode.required;
  if (Array.isArray(schemaNode?.oneOf)) {
    const activeIndex = findActiveBranchIndex(schemaNode, dataAtNode);
    const branch = activeIndex >= 0 ? schemaNode.oneOf[activeIndex] : undefined;
    if (Array.isArray(branch?.required)) return branch.required;
  }
  return [];
}

function isOptionalEmptyParent(instancePath: string, data: any, schema: any): boolean {
  if (!instancePath) return false;
  const parts = instancePath.split("/").filter(Boolean);
  if (parts.length === 0) return false;

  // Walk data + schema in lockstep so navigateSchema can route oneOf.
  let currentData: any = data;
  let currentSchema: any = schema;
  for (const part of parts) {
    if (!currentData || typeof currentData !== "object") return false;
    currentSchema = navigateSchema(currentSchema, currentData, part);
    currentData = currentData[part];
  }

  const parentKey = parts[parts.length - 1];
  const parentPath = parts.slice(0, -1);
  let parentSchemaContext: any = schema;
  let parentDataContext: any = data;
  for (const part of parentPath) {
    parentSchemaContext = navigateSchema(parentSchemaContext, parentDataContext, part);
    parentDataContext = parentDataContext?.[part];
  }
  // An array element is intentionally present; its required children always
  // block, so never treat a missing field inside an array item as optional.
  if (/^\d+$/.test(parentKey) && Array.isArray(parentDataContext)) return false;
  const parentRequired = getRequired(parentSchemaContext, parentDataContext);
  if (parentRequired.includes(parentKey)) return false;

  if (!currentData || typeof currentData !== "object") return true;
  if (Array.isArray(currentData)) return currentData.length === 0;
  const values = Object.values(currentData);
  if (values.length === 0) return true;
  return values.every((v) => v === null || v === undefined || v === "");
}

// Compiling an Ajv validator is the expensive step; cache it per schema object
// so validation on each draft change only invokes the (cheap) compiled fn.
const validatorCache = new WeakMap<object, ((data: any) => boolean) | null>();

function getValidator(schema: any): ((data: any) => boolean) | null {
  const cached = validatorCache.get(schema);
  if (cached !== undefined) return cached;

  const ajv = createAjv({ allErrors: true, strict: false, useDefaults: false });
  ajv.addKeyword({ keyword: "use_endpoint" });
  ajv.addKeyword({ keyword: "__optionalSection" });
  ajv.addKeyword({ keyword: "__fromAdditionalProperties" });
  ajv.addKeyword({ keyword: "__anyOfTransformed" });
  // Backend emits non-standard integer formats (`uint64`, etc.). Register them
  // as permissive no-ops so Ajv doesn't log "unknown format" each compile.
  ["uint64", "uint32", "uint16", "uint8", "int64", "int32", "int16", "int8"].forEach((f) => {
    ajv.addFormat(f, { type: "number", validate: () => true });
  });

  let validate: ((data: any) => boolean) | null;
  try {
    validate = ajv.compile(stripFlags(schema)) as (data: any) => boolean;
  } catch {
    validate = null;
  }
  validatorCache.set(schema, validate);
  return validate;
}

export function validateDraft(opts: {
  draft: any;
  schema: any;
}): ValidationResult {
  const { draft, schema } = opts;
  if (!schema || !schema.properties) {
    return { errors: [], blockingErrors: [], errorsByRowId: new Map() };
  }

  const validate = getValidator(schema);
  if (!validate) {
    return { errors: [], blockingErrors: [], errorsByRowId: new Map() };
  }

  validate(draft ?? {});
  const validateErrors = (validate as { errors?: any[] }).errors;
  const rawErrors: any[] = Array.isArray(validateErrors) ? validateErrors : [];

  const filteredBranch = filterOneOfBranchErrors(rawErrors, schema, draft);

  const errors: FormError[] = filteredBranch.map((e) => {
    const rowId = instancePathToRowId(
      e.instancePath ?? "",
      e.keyword === "required" ? e.params?.missingProperty : undefined
    );
    return {
      rowId,
      message: e.message ?? "Invalid value",
      raw: e,
    };
  });

  // Only missing-required fields block submit; type/enum/etc. are surfaced as
  // non-blocking warnings and deferred to the backend, matching the prior form.
  const blockingErrors = errors.filter((e) => {
    if (e.raw.keyword !== "required") return false;
    if (isOptionalEmptyParent(e.raw.instancePath ?? "", draft, schema)) return false;
    return true;
  });

  const blockingRowIds = new Set(blockingErrors.map((e) => e.rowId));
  const errorsByRowId = new Map<string, { message: string; severity: ErrorSeverity }>();
  for (const e of errors) {
    if (errorsByRowId.has(e.rowId)) continue;
    const severity: ErrorSeverity = blockingRowIds.has(e.rowId) ? "error" : "warning";
    errorsByRowId.set(e.rowId, { message: e.message, severity });
  }

  return { errors, blockingErrors, errorsByRowId };
}

export { pathToId };
